// PARITY-0: the board settings sheet's tabs as a registry. Each wave-1 feature that owns a slice
// of a board's settings (statuses, fields, automations, forms, sharing, …) registers its own tab
// here instead of editing the sheet; the sheet (board-settings-sheet.tsx) draws whatever is
// registered, sorted by order then id. "General" (general-tab.tsx) is order 0.
import type { ComponentType } from "react";
import type { Api } from "../../../api/client";
import { orderedRegistry } from "../../../extensions/ordered-registry";
import type { CompanyAccess } from "../../company";
import type { Board } from "../view-engine";

/** What the viewer may do on the board: `edit` its content, `manage` its settings. */
export type BoardSettingsCan = { edit: boolean; manage: boolean };

export type BoardSettingsTabProps = {
  board: Board;
  can: BoardSettingsCan;
  /**
   * wp/ACTIONS: writes the board's whole status set (`PATCH boards/{id}` `status_set`); rejects
   * with the server's problem (a removed status still holding tasks, no done status).
   */
  saveStatuses?: ((set: Board["status_set"]) => Promise<unknown>) | undefined;
  /** wp/ACTIONS: the API and company, for tabs that read and write on their own (Fields). */
  api?: Api | undefined;
  co?: string | undefined;
  /** T6-BOARD-NOTE: the company (the rule builder the "How it works" tab opens needs it). */
  company?: CompanyAccess | undefined;
};

export type BoardSettingsTab = {
  id: string;
  title: string;
  /** Lower first; ties by id. */
  order: number;
  Component: ComponentType<BoardSettingsTabProps>;
  /** Shown only when this says so (absent: always). */
  when?: (props: BoardSettingsTabProps) => boolean;
};

const tabs = orderedRegistry<BoardSettingsTab>("board settings tab");

/** Adds a tab; throws when its id is taken. Returns the unregister function. */
export function registerBoardSettingsTab(tab: BoardSettingsTab): () => void {
  return tabs.register(tab);
}

/** Every registered tab, sorted by order, then id. */
export function boardSettingsTabs(): BoardSettingsTab[] {
  return tabs.list();
}

/** The tabs this viewer sees on this board (each tab's `when`). */
export function visibleBoardSettingsTabs(props: BoardSettingsTabProps): BoardSettingsTab[] {
  return tabs.list().filter((t) => !t.when || t.when(props));
}
